<template>
  <ExternalLinkModal ref="externalModal" />
  <div
    v-for="notification in notificationStore.getNotifications"
    :key="notification.id"
  >
    <ErrorPopupNotification
      v-if="notification.isError"
      :id="notification.id"
      :title="t(notification.title)"
      :subtitle="t(notification.message)"
    />
    <PopupNotification
      v-if="!notification.isError"
      :show="true"
      :title="t(notification.title)"
      :subtitle="t(notification.message)"
      :autoclose="true"
    />
  </div>

  <div v-if="isLoading">
    <LoaderIcon></LoaderIcon>
  </div>
  <div v-else class="flex flex-col min-h-screen">
    <AuthValidateOptinModal
      :open="isNotVerified"
      @is-verified="isNotVerified = false"
    />
    <Header />
    <div
      role="main"
      :class="[
        route.meta.noHeader
          ? ''
          : 'mt-5 px-2 pb-8 mb-16 xl:max-w-screen-xl lg:max-w-screen-lg md:max-w-screen-md',
        'relative w-full mx-auto flex-auto h-full',
      ]"
    >
      <router-view></router-view>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "./store/v2/user";
import { useClientStore } from "./store/v2/client";
import Header from "./components/v2/layout/header/Header.vue";
import { useNotificationStore } from "./store/v2/notification";
import ErrorPopupNotification from "./components/v2/layout/ErrorPopupNotification.vue";
import LoaderIcon from "./components/v2/loader/LoaderIcon.vue";
import ExternalLinkModal from "./components/v2/layout/ExternalLinkModal.vue";
import AuthValidateOptinModal from "./components/v2/login/AuthValidateOptinModal.vue";
import { useI18n } from "vue-i18n";
import { useCookieConsent } from "./plugins/useCookieConsent";

import PopupNotification from "./components/v2/layout/PopupNotification.vue";
const userStore = useUserStore();
const clientStore = useClientStore();
const notificationStore = useNotificationStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { appContext } = getCurrentInstance()!;

const isLoading = ref(true);
const externalModal = ref();
const userAuthenticator = appContext.config.globalProperties.$userAuthenticator;
const userManager = appContext.config.globalProperties.$userManager;
const isNotVerified = ref(false);
const instance = getCurrentInstance();
const { installPlugins } = useCookieConsent(instance, clientStore);

const updateOptInModal = () => {
  isNotVerified.value = Boolean(
    userStore.getId &&
      userStore.getIsOptedIn === false &&
      !userStore.getIsSbkUser
  );
};

watch(
  () => [userStore.getId, userStore.getIsOptedIn, userStore.getIsSbkUser],
  updateOptInModal
);

onMounted(async () => {
  if (userAuthenticator.isAccessTokenValid()) {
    await userStore.getUser();
    updateOptInModal();
    await clientStore
      .getClient(userStore.getClientCode, route.params.slug, true)
      .catch(() => {
        userAuthenticator.logout();
        router.push({
          name: "login",
        });
      });

    if (
      clientStore.getIsMaintenance &&
      userStore.getCanBypassMaintenance !== true
    ) {
      await router.push({
        name: "maintenancePage",
      });
    } else if (!clientStore.getDesktopVersionActivated) {
      // Redirect if desktop version is disabled
      userAuthenticator.logout(false);

      await router.push({
        name: "desktopVersionDeactivated",
      });
    }
  }

  if (userStore.getLanguage === null) {
    userStore.setLanguage(userManager.getDefaultLanguage());
  }

  isLoading.value = false;

  document.body.addEventListener("click", (e) => {
    let target: HTMLAnchorElement | null = null;
    if (e.target && (e.target as Element).closest) {
      const el = e.target as Element;
      const anchor = el.closest("a");
      if (anchor instanceof HTMLAnchorElement) {
        target = anchor;
      }
    }
    if (!target || !target.href) {
      return;
    }
    const link = event.target.closest("a");
    // Skip modal if link has data-no-external-modal attribute
    if (link && link.hasAttribute("data-no-external-modal")) {
      // Do not show the external modal
      return;
    }

    const currentHost = window.location.host;
    const targetHost = new URL(target.href).host;

    // Check if link is external
    if (currentHost !== targetHost) {
      e.preventDefault();

      externalModal.value?.openModal(target.href);
    }
  });

  if (userStore.getTrackingAcceptedAt) {
    installPlugins();
  }
});

const notifications = computed(() => notificationStore.getNotifications);
</script>
